'use client';

import classNames from 'classnames';
import { memo, useMemo, useRef } from 'react';
import { useIntersection } from 'react-use';

import { useTranslations } from '../../i18n/TranslationsProvider';
import { CommonVideoBlockProps } from '../../types/schemas/objects/CommonVideoBlockProps';
import VideoEmbedAutoDetect from './videoEmbedAutoDetect/VideoEmbedAutoDetect';

export type VideoBlockProps = CommonVideoBlockProps & {
	url?: string;
	_type?: 'VideoBlock' | 'BackgroundVideoBlock';
	aspectRatio?: '4:3' | '16:9';
	cloudinaryHlsEnabled?: boolean;
	className?: string;
};

type PlaybackOptions = Pick<
	VideoBlockProps,
	'controls' | 'muted' | 'autoplay' | 'playsinline' | 'pause'
>;

export const VideoBlock = ({
	className,
	_type,
	aspectRatio = '16:9',
	...props
}: VideoBlockProps) => {
	const { t } = useTranslations();

	const mediaRef = useRef<HTMLDivElement>(null);
	const intersection = useIntersection(mediaRef as React.RefObject<HTMLDivElement>, {});
	const isBackgroundVideo = _type === 'BackgroundVideoBlock';

	const playbackOptions: PlaybackOptions = useMemo(
		() =>
			isBackgroundVideo
				? {
						controls: false,
						muted: true,
						autoplay: true,
						playsinline: true,
					}
				: {
						pause: props.pause || !intersection?.isIntersecting,
						muted: props.autoplay || props.muted,
					},
		[isBackgroundVideo, props.pause, props.autoplay, props.muted, intersection?.isIntersecting],
	);

	return (
		<div
			ref={mediaRef}
			className={classNames(
				'relative w-full overflow-hidden h-0',
				(isBackgroundVideo || !props.controls) && 'pointer-events-none',
				aspectRatio === '4:3' && 'pt-[75%]', // 3/4 = 0.75 = 75%
				aspectRatio === '16:9' && 'pt-[56.25%]', // 9/16 = 0.5625 = 56.25%
				className,
			)}
		>
			{!props.url ? (
				<p className="absolute inset-0 flex italic justify-center items-center">
					{t.general.video.videoUrlNotProvided}
				</p>
			) : (
				<VideoEmbedAutoDetect className="absolute inset-0" {...props} {...playbackOptions} />
			)}
		</div>
	);
};

VideoBlock.displayName = 'VideoBlock';

export default memo<VideoBlockProps>(VideoBlock);
